import qs from 'query-string';
import { Loan } from '../types/loan/Loan';
import { LoanStatus } from '../types/loan/LoanStatus';
import { LoanReviewStatus } from '../types/loan/LoanReviewStatus';
import { LoanCurrency } from '../types/loan/LoanCurrency';
import { LoanRefIndex } from '../types/enums/loan/LoanRefIndex';
import { Rating } from '../types/enums/Rating';
import { PaginationResult } from '../types/PaginationResult';
import { fetchSafe } from './fetch.safe';
import { LoanFieldValues } from '../types/loan/LoanFieldValues';
import { SaveLoanModel } from '../types/loan/SaveLoanModel';
import { EditLoanPopupModel } from '../types/loan/EditLoanFormModel';
import { responseType } from '../constants';
import { DateTimeStamp } from '../types/primitives/DateTimeStamp';

export interface LoanSearchParams {
    page?: number;
    pageSize?: number;
    orderByColumn?: string;
    sortOrder?: string;
    status?: LoanStatus[];
    review?: LoanReviewStatus[];
    analystUserId?: number[];
    currencyCode?: LoanCurrency[];
    issuers?: string[];
    arrangerReferenceNames?: string[];
    coArrangerReferenceNames?: string[];
    pricingDateFrom?: string | null;
    pricingDateTo?: string | null;
    covLite?: boolean;
    dataroom?: string[];
    dueDateFrom?: DateTimeStamp | null;
    dueDateTo?: DateTimeStamp | null;
    industries?: string[];
    useOfProceeds?: string[];
    maturityFrom?: string | null;
    maturityTo?: string | null;
    refIndex?: LoanRefIndex[];
    termFrom?: number;
    termTo?: number;
    ratings?: Rating[];
    cfRatings?: Rating[];
}

function searchLoans(params: LoanSearchParams): Promise<PaginationResult<Loan>> {
    const query = qs.stringify(params, { skipNull: true, skipEmptyString: true });
    return fetchSafe(`/api/loans${query ? `?${query}` : ''}`);
}

function exportLoans(params: LoanSearchParams): Promise<{ blob: Blob; name: string }> {
    const query = qs.stringify(params, { skipNull: true, skipEmptyString: true });
    return fetchSafe(`/api/loans/export${query ? `?${query}` : ''}`, {
        responseType: responseType.file,
    });
}

function getLoanFieldValues(): Promise<LoanFieldValues> {
    return fetchSafe('/api/loans/summary', {}, true);
}

function getLoansTotalCount(): Promise<number> {
    return fetchSafe('/api/loans/total-count');
}

function deleteLoan(id: number): Promise<void> {
    return fetchSafe(`/api/loans/${id}`, {
        method: 'DELETE',
        responseType: responseType.none
    });
}

function getLoan(id: number): Promise<Loan> {
    return fetchSafe(`/api/loans/${id}`);
}

function createLoan(model: SaveLoanModel): Promise<Loan> {
    return fetchSafe('/api/loans', {
        method: 'POST',
        body: JSON.stringify(model),
    });
}

function updateLoan(id: number, model: SaveLoanModel): Promise<Loan> {
    return fetchSafe(`/api/loans/${id}`, {
        method: 'PUT',
        body: JSON.stringify(model),
    });
}

function editPopup(id: number, model: EditLoanPopupModel): Promise<void> {
    return fetchSafe(`/api/loans/${id}/popup`, {
        method: 'PATCH',
        body: JSON.stringify(model),
        responseType: responseType.none,
    });
}

export const loanService = {
    searchLoans,
    exportLoans,
    getLoanFieldValues,
    getLoansTotalCount,
    deleteLoan,
    getLoan,
    createLoan,
    updateLoan,
    editPopup,
};
